/* eslint-disable camelcase */
import styled from 'styled-components';
import React, {
  useState,
  useMemo,
  useCallback,
  useContext,
  Dispatch,
  SetStateAction,
  useEffect,
} from 'react';
import {
  ChannelList,
  useChatContext,
  type ChannelPreviewUIComponentProps,
  type ChannelListMessengerProps,
} from 'stream-chat-react';
import type {
  ChannelFilters,
  ChannelOptions,
  Channel as ChannelType,
  MessageResponse,
} from 'stream-chat';
import {
  ContextualChatContext,
  LeftSidePanelPageType,
  ChannelType as BBChannelType,
} from 'contexts';
import { useBreakpoint } from 'hooks';
import { Icon, Title, Text, Empty, Button, theme } from 'components';
import { useInfiniteQuery } from '@tanstack/react-query';
import Loading from '@app/react-common/Loading';
import { ChannelListPeopleItem } from './ChannelListPeopleItem';
import ChannelListListingItem from './ChannelListListingItem';
import ChannelSearchMessageItem from './ChannelSearchMessageItem';
import SearchInputBase from './SearchInputBase';
import CustomChannelListContainer from './CustomChannelListContainer';

enum FilterEnum {
  all = 'All',
  myListings = 'My listings',
  properties = 'Properties',
  people = 'People',
  archived = 'Archived',
}

type filterType = keyof typeof FilterEnum;

const SEARCH_LIMIT = 100;
const CHANNEL_LIST_LIMIT = 20;

const CHANNEL_OPTIONS: ChannelOptions = {
  state: true,
  watch: true,
  limit: CHANNEL_LIST_LIMIT,
};

interface MessageSearchResult {
  channel: ChannelType;
  message: MessageResponse;
}

type SearchResult =
  | { type: 'channel'; data: ChannelType }
  | { type: 'message'; data: MessageSearchResult }
  | { type: 'section-header'; title: string };

interface IChannelListPanel {
  setHighlightedId: Dispatch<SetStateAction<string | null>>;
}

const ChannelListPanel = ({ setHighlightedId }: IChannelListPanel) => {
  const { md, lg } = useBreakpoint();
  const { client } = useChatContext();
  const [channelFilter, setChannelFilter] = useState<FilterEnum>(
    FilterEnum.all,
  );
  const {
    setChannel,
    setLeftSidePanelPage,
    expandedChannel,
    setExpandedChannel,
  } = useContext(ContextualChatContext);
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filters = useMemo(() => {
    if (!client?.userID) return {};
    let filter: ChannelFilters = {
      members: { $in: [client.userID] },
      // We only show unarchived channels by default as per design's request https://honeywell.atlassian.net/browse/BET-1928?focusedCommentId=11530429
      archived: false,
    };

    if (channelFilter === FilterEnum.myListings) {
      // Channels that showing agents have started with me about my listing https://honeywell.atlassian.net/browse/BET-1639?focusedCommentId=10943408
      filter = {
        ...filter,
        type: 'listing',
      };
    } else if (channelFilter === FilterEnum.properties) {
      // Channels that I have started with other agents https://honeywell.atlassian.net/browse/BET-1639?focusedCommentId=10943408
      filter = {
        ...filter,
        type: 'listing',
        created_by_id: client.userID,
      };
    } else if (channelFilter === FilterEnum.people) {
      filter = {
        ...filter,
        type: 'messaging',
      };
    } else if (channelFilter === FilterEnum.archived) {
      filter = {
        ...filter,
        archived: true,
      };
    }

    return filter;
  }, [channelFilter, client.userID]);

  const channelRenderFilterFn = useCallback(
    (channels: ChannelType[]) => {
      let filtered = channels;

      if (channelFilter === FilterEnum.myListings) {
        filtered = filtered.filter(
          channel => channel.data?.created_by?.id !== client?.userID,
        );
      } else if (channelFilter === FilterEnum.properties) {
        filtered = filtered.filter(
          channel => channel.data?.created_by?.id === client?.userID,
        );
      } else if (channelFilter === FilterEnum.people) {
        filtered = filtered.filter(channel => channel.type === 'messaging');
      }

      return filtered;
    },
    [channelFilter, client.userID],
  );

  const {
    data: channelPages,
    isFetching: isFetchingChannels,
    fetchNextPage: fetchNextChannels,
    hasNextPage: hasNextChannels,
    isFetchingNextPage: isFetchingNextChannels,
  } = useInfiniteQuery({
    enabled: !!searchQuery && !!client?.userID,
    queryKey: ['channelList', searchQuery, channelFilter, client?.userID],
    queryFn: async ({ pageParam = 0 }) => {
      let searchFilter: ChannelFilters = {
        members: { $in: [client.userID] },
        archived: false,
      };

      if (channelFilter === FilterEnum.myListings) {
        searchFilter = { ...searchFilter, type: 'listing' };
      } else if (channelFilter === FilterEnum.properties) {
        searchFilter = {
          ...searchFilter,
          type: 'listing',
          created_by_id: client.userID,
        };
      } else if (channelFilter === FilterEnum.people) {
        searchFilter = { ...searchFilter, type: 'messaging' };
      } else if (channelFilter === FilterEnum.archived) {
        searchFilter = { ...searchFilter, archived: true };
      }

      const channelSearchFilter: ChannelFilters = {
        ...searchFilter,
        $or: [
          { name: { $autocomplete: searchQuery } },
          { 'member.user.name': { $autocomplete: searchQuery } },
        ],
      };

      let channelsResponse = await client.queryChannels(
        channelSearchFilter,
        [
          {
            last_message_at: -1,
          },
          { updated_at: -1 },
        ],
        {
          limit: SEARCH_LIMIT,
          offset: pageParam,
        },
      );

      if (channelFilter === FilterEnum.myListings) {
        channelsResponse = channelsResponse.filter(
          channel => channel.data?.created_by?.id !== client.userID,
        );
      }

      const results: SearchResult[] = [];
      if (channelsResponse.length > 0) {
        channelsResponse.forEach(channel => {
          results.push({ type: 'channel', data: channel });
        });
      }

      return results;
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.flat().length;
      return lastPage.length === SEARCH_LIMIT ? totalFetched : undefined;
    },
  });

  const {
    data: messagePages,
    isFetching: isFetchingMessages,
    fetchNextPage: fetchNextMessages,
    hasNextPage: hasNextMessages,
    isFetchingNextPage: isFetchingNextMessages,
  } = useInfiniteQuery({
    enabled: !!searchQuery && !!client?.userID,
    queryKey: [
      'messageList',
      searchQuery,
      channelFilter,
      client?.userID,
      showArchived,
    ],
    queryFn: async ({ pageParam = 0 }) => {
      let searchFilter: ChannelFilters = {
        members: { $in: [client.userID] },
        archived: false,
      };

      if (channelFilter === FilterEnum.myListings) {
        searchFilter = { ...searchFilter, type: 'listing' };
      } else if (channelFilter === FilterEnum.properties) {
        searchFilter = {
          ...searchFilter,
          type: 'listing',
          created_by_id: client.userID,
        };
      } else if (channelFilter === FilterEnum.people) {
        searchFilter = { ...searchFilter, type: 'messaging' };
      } else if (channelFilter === FilterEnum.archived) {
        searchFilter = { ...searchFilter, archived: true };
      }

      const messageSearchResponse = await client.search(
        searchFilter,
        searchQuery,
        {
          limit: SEARCH_LIMIT,
          offset: pageParam,
        },
      );

      let filteredMessages = messageSearchResponse.results;
      if (channelFilter === 'My listings') {
        filteredMessages = filteredMessages.filter(result => {
          const channelData = result.message?.channel;
          return channelData?.created_by?.id !== client.userID;
        });
      }

      const results: SearchResult[] = [];
      if (filteredMessages.length > 0) {
        filteredMessages.forEach(result => {
          if (result.message?.channel) {
            const channel = client.channel(
              result.message.channel.type,
              result.message.channel.id,
            );
            results.push({
              type: 'message',
              data: {
                channel,
                message: result.message,
              },
            });
          }
        });
      }

      return results;
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.flat().length;
      return lastPage.length === SEARCH_LIMIT ? totalFetched : undefined;
    },
  });

  const searchResults: SearchResult[] = [
    ...(channelPages?.pages.flat() ?? []),
    ...(messagePages?.pages.flat() ?? []),
  ];

  const isFetching = isFetchingChannels || isFetchingMessages;
  const isFetchingNextPage = isFetchingNextChannels || isFetchingNextMessages;
  const hasNextPage = hasNextChannels || hasNextMessages;

  const handleChannelSelect = useCallback(
    async (channel: ChannelType, messageId?: string) => {
      setChannel(channel);

      // Pre-fetch message for faster navigation
      if (messageId) {
        await channel.state
          .loadMessageIntoState(messageId)
          .catch(console.error);

        setHighlightedId(messageId);
      }

      setSearchQuery('');
    },
    [setChannel, setHighlightedId],
  );

  const highlightText = useCallback((text: string, query: string) => {
    if (!query.trim()) return [text];
    const regex = new RegExp(
      `(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
      'gi',
    );
    return text.split(regex);
  }, []);

  const renderSearchResult = useCallback(
    ({ item }: { item: SearchResult }, idx: number) => {
      if (item.type === 'section-header') {
        return (
          <SearchSectionHeader block>
            {item.title.toUpperCase()}
          </SearchSectionHeader>
        );
      }

      if (item.type === 'channel') {
        const channel = item.data;

        return (
          <>
            {channel.type === BBChannelType.messaging && (
              <ChannelListPeopleItem
                channel={channel}
                userId={client.userID}
                key={`${channel.id}-${idx}`}
                expanded={expandedChannel}
                isSearchItem
              />
            )}
            {channel.type === BBChannelType.listing && (
              <ChannelListListingItem
                channel={channel}
                userId={client.userID}
                key={`${channel.id}-${idx}`}
                expanded={expandedChannel}
                isSearchItem
              />
            )}
          </>
        );
      }

      if (item.type === 'message') {
        const { channel, message } = item.data;

        const textParts = highlightText(message.text || '', searchQuery);

        return (
          <ChannelSearchMessageItem
            channel={channel}
            message={message}
            userId={client.userID}
            key={`${channel.id}-${message.id}-${idx}`}
            textParts={textParts}
            debouncedQuery={searchQuery}
            handleChannelSelect={handleChannelSelect}
          />
        );
      }
    },
    [
      client.userID,
      expandedChannel,
      handleChannelSelect,
      highlightText,
      searchQuery,
    ],
  );

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (!hasNextPage) {
      return;
    }

    if (scrollHeight - scrollTop <= clientHeight + 50) {
      if (hasNextChannels) fetchNextChannels();
      if (hasNextMessages) fetchNextMessages();
    }
  };

  useEffect(() => {
    const container = document.querySelector('#toggleScroll');
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return;
      e.preventDefault();
      container.scrollLeft += e.deltaY;
    };

    container.addEventListener('wheel', handleWheel);
    return () => container.removeEventListener('wheel', handleWheel);
  }, [expandedChannel]);

  useEffect(() => {
    if (!lg) {
      setExpandedChannel(true);
    }
  }, [lg, setExpandedChannel]);

  const ListComponent = useCallback(
    (props: React.PropsWithChildren<ChannelListMessengerProps>) => (
      <CustomChannelListContainer {...props}>
        {props.children}
      </CustomChannelListContainer>
    ),
    [],
  );

  const PreviewComponent = useCallback(
    (props: ChannelPreviewUIComponentProps) => (
      <>
        {props.channel.type === BBChannelType.messaging && (
          <ChannelListPeopleItem
            {...props}
            userId={client.userID}
            expanded={expandedChannel}
          />
        )}
        {props.channel.type === BBChannelType.listing && (
          <ChannelListListingItem
            {...props}
            userId={client.userID}
            expanded={expandedChannel}
          />
        )}
      </>
    ),
    [client.userID, expandedChannel],
  );

  return (
    <StyledContainer grow={!md} expanded={expandedChannel}>
      <StyledCard>
        {lg && (
          <StyledListingDetailsButtonClose
            onClick={() => {
              if (searchQuery) {
                setSearchQuery('');
              }
              setExpandedChannel(!expandedChannel);
            }}
            type="default"
            shape="round"
            size="middle"
          >
            {!expandedChannel ? 'Messages' : undefined}
            <Icon
              type={
                !expandedChannel
                  ? 'antdDoubleRightOutlined'
                  : 'antdDoubleLeftOutlined'
              }
              color="primary"
            />
          </StyledListingDetailsButtonClose>
        )}
        {expandedChannel && (
          <>
            <StyledPrimaryHeader>
              <Title level={2} inline>
                Messages
              </Title>
              <HeaderIconContainer>
                <StyledAction
                  type="fillingOutline"
                  width={18}
                  color="primary"
                  onClick={() =>
                    setLeftSidePanelPage(LeftSidePanelPageType.NewChat)
                  }
                />
              </HeaderIconContainer>
            </StyledPrimaryHeader>
            <SearchInputBase
              defaultValue={searchQuery}
              onSearch={debouncedQuery => {
                setSearchQuery(debouncedQuery);
              }}
            />
            <ToggleContainer id="toggleScroll">
              {Object.keys(FilterEnum).map((filter, idx) => {
                const filterKey = filter as filterType;
                return (
                  <StyledToggleButton
                    key={`${filter}-${idx}`}
                    $selected={channelFilter === FilterEnum[filterKey]}
                    onClick={() => setChannelFilter(FilterEnum[filterKey])}
                  >
                    {FilterEnum[filterKey]}
                  </StyledToggleButton>
                );
              })}
            </ToggleContainer>
          </>
        )}
      </StyledCard>

      <ChannelListContainer
        onScroll={event => {
          if (searchQuery) {
            handleScroll(event);
          }
        }}
      >
        {searchQuery ? (
          <>
            {isFetching && !isFetchingNextPage ? (
              <Loading />
            ) : (
              <>
                {searchResults.length ? (
                  <>
                    <StyledResultsContainer>
                      {searchResults.map((result, idx) =>
                        renderSearchResult({ item: result }, idx),
                      )}
                    </StyledResultsContainer>
                    {isFetchingNextPage && <Loading />}
                  </>
                ) : (
                  <Empty description="No results found" />
                )}
              </>
            )}
          </>
        ) : (
          <ChannelList
            filters={filters}
            sendChannelsToList
            List={ListComponent}
            LoadingIndicator={() => <Loading />}
            EmptyStateIndicator={() => (
              <EmptyChannelListContainer>
                <Icon type="newConversation" width={144} height={101} />
                <Text>No conversations yet</Text>
                <Button
                  type="link"
                  onClick={() =>
                    setLeftSidePanelPage(LeftSidePanelPageType.NewChat)
                  }
                >
                  Start a chat
                </Button>
              </EmptyChannelListContainer>
            )}
            channelRenderFilterFn={channelRenderFilterFn}
            options={CHANNEL_OPTIONS}
            // Prevents making an active user. This is important to avoid concurrent user limit
            setActiveChannelOnMount={false}
            Preview={PreviewComponent}
          />
        )}
      </ChannelListContainer>
    </StyledContainer>
  );
};

const StyledListingDetailsButtonClose = styled(Button)`
  position: relative;
  display: flex;
  align-items: center;
  padding: 4px 8px;
  color: ${theme.getColor('primary')};
  margin-bottom: 15px;
  margin-left: auto;
`;

const StyledContainer = styled.div<{ expanded: boolean; grow?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  max-width: ${({ grow, expanded }) =>
    grow ? undefined : expanded ? '385px' : '165px'};
  min-width: ${({ grow, expanded }) =>
    grow ? undefined : expanded ? '385px' : '165px'};
  border-right: 1px solid #e3e3e3;
  height: 100%;
  overflow: hidden;
`;

const ToggleContainer = styled.div`
  margin-right: -20px;
  margin-left: -20px;
  padding-right: 20px;
  padding-left: 20px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  overflow-x: auto;
  align-items: center;
  gap: 7px;
  margin-top: 15px;
  justify-content: left;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledToggleButton = styled.button<{ $selected: boolean }>`
  padding: 4px 10px;
  background-color: ${({ $selected }) => ($selected ? '#2B2B2B' : '#F0F0F0')};
  color: ${({ $selected }) => ($selected ? '#FFFFFF' : '#2B2B2B')};
  border: none;
  border-radius: 15px;
  font-weight: 600;
  white-space: nowrap;
`;

const HeaderIconContainer = styled.div<{ padButton?: boolean }>`
  display: flex;
  gap: 10px;
  flex-direction: row;
  padding-right: ${({ padButton }) => (padButton ? '35px' : undefined)};
`;

const StyledCard = styled.div`
  padding: 15px 20px;
`;

const StyledPrimaryHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledAction = styled(Icon)`
  margin-left: 10px;
`;

const ChannelListContainer = styled.div`
  border: none;
  flex: 1;
  overflow-y: auto;
`;

const StyledResultsContainer = styled.div`
  overflow-x: auto;
`;

const SearchSectionHeader = styled(Text)`
  font-weight: 600;
  padding: 12px 16px 8px 16px;
  color: #8e8e93;
`;

const EmptyChannelListContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  height: 100%;
`;

export default ChannelListPanel;
